import { Link } from '..';
import { ReactRenderer } from '@tiptap/react';
import { BubbleMenuPluginProps, BubbleMenuView } from '@tiptap/extension-bubble-menu';
import { LinkEditorBubble } from './LinkEditorBubble';
import type { LinkEditorState } from './LinkEditorBubble';
import type { Placement } from 'tippy.js';
import type { EditorState } from 'prosemirror-state';
import { Plugin, PluginKey } from 'prosemirror-state';
import type { EditorView } from 'prosemirror-view';
import debounce from 'lodash/debounce';

declare module '@tiptap/core' {
   interface Commands<ReturnType> {
      editableLink: {
         showLinkEditor: (state: LinkEditorState) => ReturnType;
         hideLinkEditor: () => ReturnType;
         editLink: () => ReturnType;
      };
   }
}

class ClickSelectBubbleMenuView extends BubbleMenuView {
   update(view: EditorView, oldState?: EditorState) {
      const hasValidSelection = true;

      if (this.updateDelay > 0 && hasValidSelection) {
         debounce(this.updateHandler, this.updateDelay)(view, oldState);
      } else {
         this.updateHandler(view, oldState);
      }
   }
}

export const ClickSelectBubbleMenuPlugin = (options: BubbleMenuPluginProps) => {
   return new Plugin({
      key:
         typeof options.pluginKey === 'string'
            ? new PluginKey(options.pluginKey)
            : options.pluginKey,
      view: view => new ClickSelectBubbleMenuView({ view, ...options }),
   });
};

export const EditableLink = Link.extend({
   inclusive: false,
   priority: -10,

   onCreate() {
      let props = { editor: this.editor };
      const duration: [number, number] = [100, 0];
      const placement: Placement = 'bottom';

      const tippyOptions = {
         duration,
         maxWidth: 'none',
         moveTransition: 'transform 0.1s ease 0.05s',
         ignoreAttributes: true,
         placement,
      };

      setTimeout(() => {
         this.storage.renderer = new ReactRenderer(LinkEditorBubble, {
            props,
            editor: this.editor,
         });

         this.storage.bubbleMenu = ClickSelectBubbleMenuPlugin({
            pluginKey: 'bubble-menu-' + this.editor.storage.editorid.editorid,
            editor: this.editor,
            element: this.storage.renderer.element,
            shouldShow: () => true,
            tippyOptions: tippyOptions,
         });

         this.editor.registerPlugin(this.storage.bubbleMenu);
      }, 100);
   },
   addCommands() {
      return {
         ...this.parent?.(),
         showLinkEditor:
            (state?: LinkEditorState) =>
            ({ dispatch }) => {
               if (dispatch && this.storage.renderer) {
                  this.storage.renderer.ref.show(state);
               }
               return true;
            },
         hideLinkEditor:
            () =>
            ({ dispatch }) => {
               if (dispatch && this.storage.renderer) {
                  this.storage.renderer.ref.hide();
               }
               return true;
            },
         editLink:
            () =>
            ({ dispatch, commands }) => {
               if (!dispatch) {
                  return true;
               }
               commands.extendMarkRange('link');
               commands.showLinkEditor('edit');
               return true;
            },
      };
   },
   addStorage() {
      return {
         renderer: null,
         bubbleMenu: null,
      };
   },
   addKeyboardShortcuts() {
      return {
         'Mod-k': () => this.editor.commands.editLink(),
      };
   },
   onSelectionUpdate() {
      if (this.editor.isActive('link')) {
         this.editor.commands.showLinkEditor(null);
      } else {
         this.editor.commands.hideLinkEditor();
      }
   },
   onExit() {
      if (this.storage.renderer) {
         this.storage.renderer.destroy();
      }
      this.editor.unregisterPlugin(this.storage.bubbleMenu);
   },
   addPasteRules() {
      return [];
   },
}).configure({ openOnClick: false, linkOnPaste: false, autolink: false });
